import type {
  ProjectFunctionGroup,
  ProjectFunctionSeries,
} from "@/hooks/useProjectPeople";

function compareGroups(
  a: ProjectFunctionGroup | null,
  b: ProjectFunctionGroup | null,
) {
  if (a?.id === b?.id) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return a.display_order - b.display_order || a.name.localeCompare(b.name);
}

function compareFunctionSeries(
  a: ProjectFunctionSeries,
  b: ProjectFunctionSeries,
) {
  return (
    a.name.localeCompare(b.name) ||
    a.required_count - b.required_count ||
    a.id.localeCompare(b.id)
  );
}

export function sortByMainFunctionGroup<
  T extends { primaryGroup: ProjectFunctionGroup | null; name: string },
>(items: T[]) {
  return [...items].sort(
    (a, b) =>
      compareGroups(a.primaryGroup, b.primaryGroup) ||
      a.name.localeCompare(b.name),
  );
}

export function getMainFunctionGroupForPerson({
  personId,
  functions,
  groupsById,
  eventDays,
}: {
  personId: string;
  functions: ProjectFunctionSeries[];
  groupsById: Map<string, ProjectFunctionGroup>;
  eventDays: string[];
}) {
  const assigned = functions.flatMap((series) =>
    series.days
      .filter((day) =>
        (day.assignments ?? []).some(
          (assignment) => assignment.person_id === personId,
        ),
      )
      .map((day) => ({
        date: day.function_date,
        series,
        group: series.project_function_group_id
          ? (groupsById.get(series.project_function_group_id) ?? null)
          : null,
      })),
  );

  for (const eventDay of eventDays) {
    const firstEventAssignment = assigned
      .filter((assignment) => assignment.date === eventDay && assignment.group)
      .sort((a, b) => compareFunctionSeries(a.series, b.series))[0];
    if (firstEventAssignment?.group) return firstEventAssignment.group;
  }

  return (
    assigned
      .filter((assignment) => assignment.group)
      .sort((a, b) => {
        if (a.date !== b.date) return a.date.localeCompare(b.date);
        return compareFunctionSeries(a.series, b.series);
      })[0]?.group ?? null
  );
}
